/**
 * Brief 26a - Compliance Registers & Forms catalog (read-only)
 *
 * Surfaces the firm's compliance registers, numbered as its policies number
 * them (BM-H3300), and the 8 mandated forms seeded by the policy registry. Two card panels; each card links to a read-only detail
 * view. Restricted to compliance stakeholders (Compliance / MLRO / director /
 * admin) by the backend — a 403 renders a not-authorised notice.
 *
 * Scope: 26a Phase 1 only. No edit/write affordances.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, FileText, AlertCircle, RefreshCw, ShieldOff, ShieldAlert, CheckCircle2, Clock, CloudOff } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

// BM-H4600 (23 Oct 2026): the card's colour is the register's status, not
// its tier: green nothing live, amber entries live, red something overdue.
// A register CaseMinder does not read has no status to show.
export function registerStatus(item) {
  if (item.held_in !== "caseminder") return null;
  if (item.overdue_count) return { tone: "red", Icon: AlertCircle, label: `${item.overdue_count} overdue` };
  if (item.open_count) return { tone: "amber", Icon: Clock, label: `${item.open_count} live` };
  return { tone: "green", Icon: CheckCircle2, label: "Clear" };
}

const TONES = {
  red: "bg-red-100 text-red-900",
  amber: "bg-amber-100 text-amber-900",
  green: "bg-green-100 text-green-900",
};

function StatusBadge({ item }) {
  const st = registerStatus(item);
  if (!st) {
    return (
      <span title="Kept in SharePoint, not read here" data-testid={`register-status-${item.ref}`} className="text-gray-400">
        <CloudOff size={14} />
      </span>
    );
  }
  const { tone, Icon, label } = st;
  return (
    <span
      data-testid={`register-status-${item.ref}`}
      data-tone={tone}
      title={label}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${TONES[tone]}`}
    >
      <Icon size={12} />
      {label}
    </span>
  );
}

// The tier (red = high-stakes register) is shown as an icon, not a colour.
function TierMark({ tier }) {
  if (tier !== "red") return null;
  return (
    <span title="High-stakes register" data-testid="register-tier-high" className="text-indigo-400">
      <ShieldAlert size={14} />
    </span>
  );
}

// BM-H3300 (30 Sep 2026): say where each register is kept. The four held in
// CaseMinder show a count read from their records; the SharePoint registers
// are not read here, so they show no count rather than a false "0 entries".
function RegisterHolding({ item }) {
  if (item.held_in === "caseminder") {
    return (
      <div className="mt-1 text-xs text-gray-600" data-testid={`register-holding-${item.ref}`}>
        Kept in CaseMinder · {item.entry_count} {item.entry_count === 1 ? "entry" : "entries"}
        {item.open_count ? ` · ${item.open_count} live` : ""}
      </div>
    );
  }
  return (
    <div className="mt-1 text-xs text-gray-500" data-testid={`register-holding-${item.ref}`}>
      Kept in SharePoint (COMPLIANCE)
    </div>
  );
}

function ArtefactCard({ item, kind }) {
  const ref = item.ref || item.register_ref || item.form_ref;
  const base = kind === "register" ? "/policy/registers" : "/policy/forms";
  const gp = item.governing_policy || {};
  return (
    <Link
      to={`${base}/${encodeURIComponent(ref)}`}
      data-testid={`${kind}-card-${ref}`}
      className="block border rounded-lg p-4 hover:bg-gray-50 hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-500">
          {ref}
          <TierMark tier={item.tier} />
        </span>
        {kind === "register" ? <StatusBadge item={item} /> : null}
      </div>
      <div className="font-medium text-gray-900">{item.name}</div>
      <div className="mt-2 text-xs text-gray-500">
        Governing policy: {gp.name || item.governing_policy_slug}
      </div>
      {kind === "register" && <RegisterHolding item={item} />}
    </Link>
  );
}

export default function RegistersForms() {
  const [registers, setRegisters] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setForbidden(false);
      try {
        const [rr, fr] = await Promise.all([
          authFetch(`${API}/api/policy/registers`, { credentials: "include" }),
          authFetch(`${API}/api/policy/forms`, { credentials: "include" }),
        ]);
        if (rr.status === 403 || fr.status === 403) {
          if (!cancelled) setForbidden(true);
          return;
        }
        if (!rr.ok) throw new Error(`registers HTTP ${rr.status}`);
        if (!fr.ok) throw new Error(`forms HTTP ${fr.status}`);
        const rj = await rr.json();
        const fj = await fr.json();
        if (!cancelled) {
          setRegisters(rj.registers || []);
          setForms(fj.forms || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="registers-forms-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading registers &amp; forms...
      </div>
    );
  }
  if (forbidden) {
    return (
      <div data-testid="registers-forms-forbidden" className="p-6 text-gray-600">
        <ShieldOff className="inline-block mr-2" size={16} />
        You do not have access to the compliance registers &amp; forms. This
        surface is restricted to Compliance, MLRO, director, and admin roles.
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="registers-forms-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load registers &amp; forms: {error}
      </div>
    );
  }

  return (
    <div data-testid="registers-forms-page" className="p-6">
      <h1 className="text-2xl font-semibold mb-6">Compliance Registers &amp; Forms</h1>

      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList size={18} />
          <h2 className="text-lg font-medium">Registers</h2>
          <span
            data-testid="registers-count"
            className="text-sm text-gray-500"
          >
            ({registers.length})
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {registers.map((r) => (
            <ArtefactCard key={r.ref} item={r} kind="register" />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <FileText size={18} />
          <h2 className="text-lg font-medium">Forms</h2>
          <span data-testid="forms-count" className="text-sm text-gray-500">
            ({forms.length})
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => (
            <ArtefactCard key={f.ref} item={f} kind="form" />
          ))}
        </div>
      </section>
    </div>
  );
}
